import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Heatmap, HeatmapResult, HeatmapVoteData, NewHeatmap, NewPoll, Poll} from '../_types/polls';
import {GenericResponse} from '../_types/shared';
import {fetchAllPages} from '../_utils/http-params';
import {jsonToFormData} from '../_utils/form-data-builder';

@Injectable({
    providedIn: 'root'
})
export class PollsService {
    private apiPoll: string = `${environment.apiUrl}/polls`;
    private apiHeatmap: string = `${environment.apiUrl}/heatmap-surveys`;

    constructor(private http: HttpClient) {
    }

    getAllPolls(communityId?: number, reducedContent?: boolean): Observable<Poll[]> {
        let parameters: HttpParams = new HttpParams();
        if (reducedContent) {
            parameters = parameters.set('only_name', 'true');
        }
        if (communityId) {
            parameters = parameters.set('community', communityId);
        }
        return fetchAllPages<Poll>(this.http, `${this.apiPoll}/`, 1000, parameters);
    }

    createNewPoll(data: NewPoll): Observable<Poll> {
        return this.http.post<Poll>(`${this.apiPoll}/`, data);
    }

    editPoll(data: Poll): Observable<Poll> {
        return this.http.patch<Poll>(`${this.apiPoll}/${data.id}/`, data);
    }

    deletePoll(pollId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiPoll}/${pollId}/`);
    }

    getPollById(pollId: number, userToken?: any): Observable<Poll> {
        let parameters: HttpParams = new HttpParams();
        if (userToken) {
            parameters = parameters.set('poll_code', userToken);
        }
        return this.http.get<Poll>(`${this.apiPoll}/${pollId}/`, {params: parameters});
    }

    votePoll(pollId: number, options: number[], options_answers?: any[], userToken?: string): Observable<GenericResponse<Poll>> {
        return this.http.post<GenericResponse<Poll>>(`${this.apiPoll}/${pollId}/votes/`, {
            options,
            options_answers,
            ...(userToken ? {poll_code: userToken} : {})
        });
    }

    createNewHeatmap(data: NewHeatmap): Observable<Heatmap> {
        const payload = {...data};
        if (data.end_at) {
            payload.end_at = new Date(data.end_at).toISOString();
        }
        const formData: FormData = jsonToFormData(payload, ['options']);
        return this.http.post<Heatmap>(`${this.apiHeatmap}/`, formData);
    }

    editHeatmap(data: Heatmap): Observable<Heatmap> {
        const payload: Partial<Heatmap> = {...data};
        if (data.end_at) {
            payload.end_at = new Date(data.end_at).toISOString();
        }
        if (payload.id) {
            delete payload.id;
        }
        if (payload.post) {
            delete payload.post;
        }
        const formData: FormData = jsonToFormData(payload, ['options']);
        return this.http.patch<Heatmap>(`${this.apiHeatmap}/${data.id}/`, formData);
    }

    deleteHeatmap(heatmapId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiHeatmap}/${heatmapId}/`);
    }

    voteHeatmap(data: HeatmapVoteData, heatmapId: number): Observable<Heatmap> {
        return this.http.post<Heatmap>(`${this.apiHeatmap}/${heatmapId}/votes/`, data);
    }

    getHeatmapResults(heatmapId: number): Observable<HeatmapResult[]> {
        return fetchAllPages<HeatmapResult>(this.http, `${this.apiHeatmap}/${heatmapId}/votes/`);
    }

    getHeatmapUserResults(heatmapId: number): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/reports/heatmap-detail/${heatmapId}/`);
    }
}
